import {
    Alert,
    Container,
    convertFromBoolean,
    convertToBoolean,
    Form,
    FormDropDown,
    FormInput,
    FormRadio,
    getPageValidationInfo,
    isNullOrWhiteSpace,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const ProfileInsuranceApplicant = (props) => {
    const sizes = useSizes();

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - mimics componentDidMount behavior
    useEffect(() => {
        props.doGetPolicy();

        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }
    }, []);

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);
    const isCertificate = props.policy?.type?.toLowerCase() === 'certificate';

    const isSeller = props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'seller';
    const isRealtor =
        props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'realtorlawyer';
    const isBuyer = props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'buyer';
    const isAdmin = props.context?.session?.userType?.toLowerCase() === 'administrator';

    // Relationship-based visibility (for non-certificate types)
    const relationshipCode = props.policy?.secureMyOfferCustomInfo?.applicantRelationshipToSellerCode?.toLowerCase();
    const isSellerApplicant = props.policy?.secureMyOfferCustomInfo?.isSeller;
    const isNotSeller = isSellerApplicant === false;

    // Real Estate Agent Contact Details: not certificate + not seller + realtor; certificate + certificateOwner is realtorlawyer
    const showRealEstateAgentContactDetails =
        (!isCertificate && isNotSeller && relationshipCode === 'realtor') || (isCertificate && isRealtor);

    // A guest partner is an agent who quoted for a client without an account. Nothing sets
    // certificateOwnerTypeCode, isSeller or the relationship on their submission, so the section
    // above can never fire and the details they gave sit on the record unreachable — their e-mail
    // is what says they exist.
    //
    // Latched, and that is the point: `realEstateAgentEmail` is both what reveals those fields and
    // one of the fields they edit. Read live, clearing it mid-edit tears the form out from under
    // the cursor — Save and Cancel with it — while the rest sit dirty in the store, so the next
    // save of ANY section writes them plus the emptied e-mail (upsertPolicy posts the whole
    // policy). The latch is per transaction: policy.id changes under this component when a broker
    // creates an endorsement or a cancellation (see containers/Profile), and a latch carried into
    // the next transaction would head an empty card "Guest Partner" on a record that has none.
    const hasRealEstateAgentDetails = !isNullOrWhiteSpace(props.policy?.secureMyOfferCustomInfo?.realEstateAgentEmail);
    const policyId = props.policy?.id;
    const [guestPartnerRevealedFor, setGuestPartnerRevealedFor] = useState(null);
    useEffect(() => {
        if (hasRealEstateAgentDetails) {
            setGuestPartnerRevealedFor(policyId ?? null);
        }
    }, [hasRealEstateAgentDetails, policyId]);

    // One value, not two flags. The realtor branch and the guest branch write the SAME four
    // secureMyOfferCustomInfo.realEstateAgent* fields, so exactly one may ever be on screen —
    // two editable copies of one answer would let the later save win silently. Deriving the
    // identity once makes that structural: there is no pair of booleans left to fall out of step,
    // and the header, label and remount key below cannot disagree about which case this is.
    const agentSectionIdentity = showRealEstateAgentContactDetails
        ? 'realEstateAgent'
        : guestPartnerRevealedFor === (policyId ?? null)
          ? 'guestPartner'
          : null;
    const isGuestPartner = agentSectionIdentity === 'guestPartner';

    // Named rather than reusing showBuyerContactDetails: the offer status is not a contact
    // detail, and a later change to when contact details appear should not silently move it.
    // Buyer Contact Details: not certificate = always visible; certificate + certificateOwner is buyer
    const showBuyerContactDetails = !isCertificate || (isCertificate && isBuyer);
    const showBuyerOfferStatus = showBuyerContactDetails;

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            <Form
                pageCode={props.pageCode}
                sectionCode='contactDetails'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='Profile_InsuranceApplicant.contactDetails' />}
                label={<FormattedMessage id='Profile_InsuranceApplicant.enterContactInformation' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormInput
                    fieldCode='firstName'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                    value={props.policy?.secureMyOfferCustomInfo?.applicantFirstName}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantFirstName', value })
                    }
                    allowEdit
                />
                <FormInput
                    fieldCode='lastName'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                    value={props.policy?.secureMyOfferCustomInfo?.applicantLastName}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantLastName', value })
                    }
                    allowEdit
                />
                <FormInput
                    fieldCode='email'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                    value={props.policy?.secureMyOfferCustomInfo?.applicantEmail}
                    type='email'
                    placeholder='someone@example.com'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantEmail', value })
                    }
                    allowEdit
                />
                <FormInput
                    fieldCode='phone'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                    value={props.policy?.secureMyOfferCustomInfo?.applicantPhone}
                    type='phone'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantPhone', value })
                    }
                    allowEdit
                />
            </Form>
            {/* One Form for one dataset, wearing whichever of the two identities applies.
                `sectionCode` stays put across both: tooltips and per-field validation are keyed on
                (pageCode, sectionCode, fieldCode), so a code of its own for the guest case would
                hide every tooltip authored here and let a validation error arrive tagged for a
                section that is not on screen — no banner, no auto-open, no way to correct the
                record. The cost, accepted: the two identities cannot carry different help text,
                and a realtor-scoped validation rule will open this card on a guest record too.
                `key` is load-bearing. Both branches now render one element at one position, so
                without it React reconciles by index and keeps the same Form instance — including
                its internal `editing` state — when the identity flips. Changing the relationship
                mid-edit would relabel the open card and leave dirty values under a live Save. */}
            {agentSectionIdentity && (
                <Form
                    key={agentSectionIdentity}
                    pageCode={props.pageCode}
                    sectionCode='realEstateAgentContactDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={
                        <FormattedMessage
                            id={
                                isGuestPartner
                                    ? 'Profile_InsuranceApplicant.guestPartner'
                                    : 'Profile_InsuranceApplicant.realEstateAgentContactDetails'
                            }
                        />
                    }
                    label={
                        <FormattedMessage
                            id={
                                isGuestPartner
                                    ? 'Profile_InsuranceApplicant.guestPartnerInformation'
                                    : 'Profile_InsuranceApplicant.realEstateAgentContactInformation'
                            }
                        />
                    }
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormInput
                        fieldCode='realEstateAgentFirstName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentFirstName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.realEstateAgentFirstName',
                                value,
                            })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentLastName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentLastName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.realEstateAgentLastName',
                                value,
                            })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentEmail'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentEmail}
                        type='email'
                        placeholder='someone@example.com'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.realEstateAgentEmail', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentPhone'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentPhone}
                        type='phone'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.realEstateAgentPhone', value })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {showBuyerContactDetails && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='buyerContactDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.buyerContactDetails' />}
                    label={<FormattedMessage id='Profile_InsuranceApplicant.buyerContactInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormInput
                        fieldCode='buyerFirstName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerFirstName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerFirstName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerLastName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerLastName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerLastName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerEmail'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerEmail}
                        type='email'
                        placeholder='someone@example.com'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerEmail', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerPhone'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerPhone}
                        type='phone'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerPhone', value })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {showBuyerOfferStatus && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='buyerOfferStatus'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.buyerOfferStatusDetails' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormRadio
                        fieldCode='buyerOfferStatusCode'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.buyerOfferStatus' />}
                        value={{ code: props.policy?.secureMyOfferCustomInfo?.buyerOfferStatusCode }}
                        references={props.reference?.references?.SecureMyOffer?.BuyerOfferStatus}
                        referencesEvent={() => props.reference.doGetReferences('SecureMyOffer.BuyerOfferStatus')}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.buyerOfferStatusCode',
                                value: value.code,
                            })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {isCertificate && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='certificateInformation'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.certificateInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormDropDown
                        fieldCode='certificateOwnerTypeCode'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.certificateOwnerType' />}
                        value={{
                            code: props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode,
                            value: props.policy.secureMyOfferCustomInfo?.certificateOwnerTypeName,
                        }}
                        references={props.reference?.references?.CertificateOwnerType}
                        referencesEvent={() => props.reference.doGetReferences('CertificateOwnerType')}
                        changeEvent={(value) => {
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.certificateOwnerTypeCode',
                                value: value.code,
                            });
                        }}
                        allowEdit={false}
                    />
                    {(isRealtor || isSeller) && (
                        <FormInput
                            fieldCode='minExpectedSaleAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.minExpectedSaleAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.minExpectedSaleAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.minExpectedSaleAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {(isRealtor || isSeller) && (
                        <FormInput
                            fieldCode='maxExpectedSaleAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.maxExpectedSaleAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.maxExpectedSaleAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.maxExpectedSaleAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {isBuyer && (
                        <FormInput
                            fieldCode='intendedOfferAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.intendedOfferAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.intendedOfferAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.intendedOfferAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {/* HIDDEN: preApprovalRangeMinAmountOverride - display-only, does NOT affect premium calculation.
                        Only PreApprovalRangeMaxAmountOverride affects premium. */}

                    {(isRealtor || isSeller) && isAdmin && (
                        <FormInput
                            fieldCode='preApprovalRangeMaxAmountOverride'
                            label={
                                <FormattedMessage id='Profile_InsuranceApplicant.preApprovalRangeMaxAmountOverride' />
                            }
                            value={props.policy?.secureMyOfferCustomInfo?.preApprovalRangeMaxAmountOverride}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.preApprovalRangeMaxAmountOverride',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}
                </Form>
            )}
            <Form
                pageCode={props.pageCode}
                sectionCode='additionalInfo'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='Profile_InsuranceApplicant.additionalInfo' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormRadio
                    fieldCode='usedRealEstateAgent'
                    label={<FormattedMessage id='SecureMyOffer.field.usedRealEstateAgent' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.usedRealEstateAgent) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.usedRealEstateAgent',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='wantsEquityAccess'
                    label={<FormattedMessage id='SecureMyOffer.field.wantsEquityAccess' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.wantsEquityAccess) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.wantsEquityAccess',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
            </Form>
        </React.Fragment>
    );
};

export default ProfileInsuranceApplicant;
